import { ToastrService } from 'ngx-toastr';
import { of, pipe } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Action, Master, actionValidation, masterValidation } from '../code';
import { State } from '../services/work.state';

export const masterRecordCheck = (toastr: ToastrService) =>
  pipe(
    switchMap(([state, currentState]: [Partial<State>, State]) => {
      if (!!state.master) {
        const master = state.master;
        const masterDefinedProps = (
          Object.keys(master) as (keyof Master)[]
        ).filter((ky) => master[ky] !== undefined && master[ky] !== null);
        if (!state.master?.recordId) {
          if (
            masterDefinedProps.filter(
              (ky) => ky !== 'campaignId' && ky !== 'phone1'
            ).length > 0
          ) {
            /*
             ** for an IB call which it the only time we should have a master state update
             ** without a recordId we should only have a campaignId and a phone1 value.
             ** if we have more then something is trying to be modified.
             */
            toastr.error(
              'Attempt to assign data without a record. Please review scripting.',
              'Blank Record',
              { enableHtml: true, disableTimeOut: true, closeButton: true }
            );

            const newState = {
              ...state,
              ...{
                master: {
                  ...currentState.master,
                  campaignId: state.master!.campaignId,
                  phone1: state.master!.phone1,
                },
                system: {
                  ...state.system,
                  ...{
                    callback: false,
                    navToQ: undefined,
                  },
                },
              },
            };
            return of([newState, currentState] as [Partial<State>, State]);
          }
        }

        const errors = masterDefinedProps.reduce((prev, ky) => {
          const validation = masterValidation[ky];
          const value = master[ky] as string;
          if (
            validation &&
            value &&
            validation.maxLength &&
            value.length > validation.maxLength
          ) {
            prev = {
              ...prev,
              [ky]: {
                ...prev[ky],
                maxLength: `Master field '${ky}' being assigned value of '${master[ky]}' is greater than the max length of ${validation.maxLength}`,
              },
            };
          }
          return prev;
        }, {} as ValidationErrors);

        const errorList = Object.keys(errors) as (keyof ValidationErrors)[];
        if (errorList.length) {
          const errorMsgs = errorList.reduce((prev, ky) => {
            return [...prev, ...Object.values(errors[ky]!)];
          }, [] as string[]);
          toastr.error(
            `Record has the following invalid conditions. Please review scripting.<ul><li>${errorMsgs.join(
              '</li><li>'
            )}</li></ul>`,
            'Record Validation',
            { enableHtml: true, disableTimeOut: true, closeButton: true }
          );
          const newState = {
            ...state,
            ...{
              master: {
                ...currentState.master,
                campaignId: state.master!.campaignId,
                phone1: state.master!.phone1,
              },
              system: {
                ...state.system,
                ...{
                  callback: false,
                  navToQ: undefined,
                },
              },
            },
          };
          return of([newState, currentState] as [Partial<State>, State]);
        }
      }
      if (!!state.action) {
        const action = state.action;
        const actionDefinedProps = (
          Object.keys(action) as (keyof Action)[]
        ).filter((ky) => action[ky] !== undefined && action[ky] !== null);

        const errors = actionDefinedProps.reduce((prev, ky) => {
          const validation = actionValidation[ky];
          const value = action[ky] as string;
          if (
            validation &&
            value &&
            validation.maxLength &&
            value.length > validation.maxLength
          ) {
            prev = {
              ...prev,
              [ky]: {
                ...prev[ky],
                maxLength: `Action field '${ky}' being assigned value of '${action[ky]}' is greater than the max length of ${validation.maxLength}`,
              },
            };
          }
          return prev;
        }, {} as ValidationErrors);

        const errorList = Object.keys(errors) as (keyof ValidationErrors)[];
        if (errorList.length) {
          const errorMsgs = errorList.reduce((prev, ky) => {
            return [...prev, ...Object.values(errors[ky]!)];
          }, [] as string[]);
          toastr.error(
            `Record has the following invalid conditions. Please review scripting.<ul><li>${errorMsgs.join(
              '</li><li>'
            )}</li></ul>`,
            'Record Validation',
            { enableHtml: true, disableTimeOut: true, closeButton: true }
          );
          const newState = {
            ...state,
            ...{
              action: {
                ...currentState.action,
              },
              system: {
                ...state.system,
                ...{
                  callback: false,
                  navToQ: undefined,
                },
              },
            },
          } as Partial<State>;
          return of([newState, currentState] as [Partial<State>, State]);
        }
      }

      return of([state, currentState] as [Partial<State>, State]);
    })
  );

type ValidationErrors = {
  [key: string]: { [validator: string]: string };
};
